import { createSlice } from "@reduxjs/toolkit";
import {
  fetchForgotPassword,
  fetchLoginUserDetails,
  fetchProviderAuthenticateWhileLogin,
  fetchProviderPinVerification,
  fetchUpdatePassword,
  fetchUpdatePhoneNumber,
  fetchUserByToken,
  fetchChangePassword,
} from "./LoginActions";

const providerLoginAuthenticate = createSlice({
  name: "providerLoginAuthenticate",
  initialState: {
    isLoginLanding: false,
    providerLoginAuth: null,
    providerLoginSectionError: null,
    needVerificationPin: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderAuthenticateWhileLogin.pending, (state) => {
        state.isLoginLanding = true;
        state.providerLoginSectionError = null;
      })
      .addCase(
        fetchProviderAuthenticateWhileLogin.fulfilled,
        (state, action) => {
          state.isLoginLanding = false;
          if (action.payload.success) {
            state.providerLoginSectionError = false;
            if (
              action.payload.data.data?.twoFactorVerificationRequiredResponseDTO
            ) {
              state.needVerificationPin = true;
              state.providerLoginAuth =
                action.payload.data.data.twoFactorVerificationRequiredResponseDTO;
            } else {
              state.needVerificationPin = false;
              state.providerLoginAuth = action.payload.data.data;
            }
          } else {
            state.providerLoginSectionError = true;
            state.providerLoginAuth = action.payload;
          }
        }
      );
  },
});

const providerPinVerification = createSlice({
  name: "providerPinVerification",
  initialState: {
    isVerificationLoading: false,
    providerVerifiedToken: null,
    providerPinVerificationError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProviderPinVerification.pending, (state) => {
        state.isVerificationLoading = true;
        state.providerPinVerificationError = null;
      })
      .addCase(fetchProviderPinVerification.fulfilled, (state, action) => {
        state.isVerificationLoading = false;
        if (action.payload.success) {
          state.providerPinVerificationError = false;
          state.providerVerifiedToken = action.payload.data.data;
        }
      })
      .addCase(fetchProviderPinVerification.rejected, (state, action) => {
        state.isVerificationLoading = false;
        state.providerPinVerificationError = action.error.message;
      });
  },
});
const providerForgotPassword = createSlice({
  name: "providerForgotPassword",
  initialState: {
    isForgotPasswordLoading: false,
    forgotPasswordData: null,
    forgotPasswordError: null,
    updatePasswordData: [],
    updatePasswordError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchForgotPassword.pending, (state) => {
        state.isForgotPasswordLoading = true;
      })
      .addCase(fetchForgotPassword.fulfilled, (state, action) => {
        state.isForgotPasswordLoading = false;
        if (action.payload.success) {
          state.forgotPasswordError = false;
          state.forgotPasswordData = action.payload.data.data;
        } else {
          state.forgotPasswordError = true;
        }
      })
      .addCase(fetchForgotPassword.rejected, (state) => {
        state.isForgotPasswordLoading = false;
        state.forgotPasswordError = true;
      });
  },
});
const providerUserDetails = createSlice({
  name: "providerUserDetails",
  initialState: {
    isUserDetailsLoading: false,
    userDetailsData: null,
    userDetailsError: null,
    userDetailsPinError: null,
    updatePasswordError: null,
    userByTokenData: null,
    userByTokenError: null,
    isUserByTokenLoading: false,
    changePasswordData: null,
    isChangePasswordLoading: false,
    changePasswordError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserByToken.pending, (state) => {
        state.isUserByTokenLoading = true;
        state.userByTokenError = null;
      })
      .addCase(fetchUserByToken.fulfilled, (state, action) => {
        console.log("action.payload", action.payload);
        state.isUserByTokenLoading = false;
        if (action.payload.success) {
          state.userByTokenError = false;
          state.userByTokenData = action.payload?.data?.data;
        } else {
          state.userByTokenError = true;
          state.userByTokenData = action.payload;
        }
      })
      .addCase(fetchUserByToken.rejected, (state) => {
        state.isUserByTokenLoading = false;
        state.userByTokenError = true;
      });
    builder
      .addCase(fetchLoginUserDetails.pending, (state) => {
        state.isUserDetailsLoading = true;
        state.userDetailsError = null;
      })
      .addCase(fetchLoginUserDetails.fulfilled, (state, action) => {
        state.isUserDetailsLoading = false;
        if (action.payload.success) {
          state.userDetailsError = false;
          state.userDetailsData = action.payload.data.data;
        } else {
          state.userDetailsError = true;
        }
      })
      .addCase(fetchLoginUserDetails.rejected, (state, action) => {
        state.isUserDetailsLoading = false;
        state.userDetailsError = action.error.message;
      });
    builder
      .addCase(fetchUpdatePhoneNumber.pending, (state) => {
        state.userDetailsPinError = null;
      })
      .addCase(fetchUpdatePhoneNumber.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.userDetailsPinError = false;
          state.userDetailsData = action.payload.data.data;
        } else {
          state.userDetailsPinError = true;
        }
      })
      .addCase(fetchUpdatePhoneNumber.rejected, (state, action) => {
        state.userDetailsPinError = action.error.message;
      });
    builder
      .addCase(fetchUpdatePassword.pending, (state) => {
        state.updatePasswordError = null;
      })
      .addCase(fetchUpdatePassword.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.updatePasswordError = null;
        } else {
          const errorMsg =
            action.payload.data?.metadata?.outcome?.additionalDetails?.[0]
              ?.innerError ||
            action.payload.data?.metadata?.outcome?.message ||
            "Password update failed";
          state.updatePasswordError = errorMsg;
        }
      })
      .addCase(fetchUpdatePassword.rejected, (state, action) => {
        state.updatePasswordError = action.error.message;
      });
    builder
      .addCase(fetchChangePassword.pending, (state) => {
        state.isChangePasswordLoading = true;
        state.changePasswordError = null;
      })
      .addCase(fetchChangePassword.fulfilled, (state, action) => {
        state.isChangePasswordLoading = false;
        if (action.payload.success) {
          state.changePasswordError = false;
          state.changePasswordData = action.payload.data.data;
        } else {
          state.changePasswordError = true;
        }
      })
      .addCase(fetchChangePassword.rejected, (state, action) => {
        state.isChangePasswordLoading = false;
        state.changePasswordError = action.error.message;
      });
  },
});

const resetPasswordSlice = createSlice({
  name: "resetPassword",
  initialState: {
    userByTokenData: null,
    isUserByTokenLoading: false,
    userByTokenError: null,
    changePasswordData: null,
    isChangePasswordLoading: false,
    changePasswordError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChangePassword.pending, (state) => {
        state.isChangePasswordLoading = true;
        state.changePasswordError = null;
      })
      .addCase(fetchChangePassword.fulfilled, (state, action) => {
        state.isChangePasswordLoading = false;
        if (action.payload.success) {
          state.changePasswordError = false;
          state.changePasswordData = action.payload.data.data;
        } else {
          state.changePasswordError = true;
        }
      })
      .addCase(fetchChangePassword.rejected, (state, action) => {
        state.isChangePasswordLoading = false;
        state.changePasswordError = action.error.message;
      });
  },
});

export const resetPasswordReducer = resetPasswordSlice.reducer;
export const providerLoginAuthenticateReducer =
  providerLoginAuthenticate.reducer;
export const providerUserDetailsReducer = providerUserDetails.reducer;
export const providerPinVerificationReducer = providerPinVerification.reducer;
export const providerForgotPasswordReducer = providerForgotPassword.reducer;
